import { EventNode } from './nodes/event-node.js';
import { instantiateGraph, toCamelCase } from './instantiate-graph.js';
import { foldConstantValueSubtrees } from './constant-fold.js';
import { VariableStore } from './variable-store.js';
export { GraphNode } from './graph-node.js';
export { Observable, resolvePortValue } from './observable.js';
export { ActionNode } from './nodes/action-node.js';
export { BranchNode } from './nodes/branch-node.js';
export { CallCustomEventNode } from './nodes/call-custom-event-node.js';
export { CallFunctionNode } from './nodes/call-function-node.js';
export { EventNode } from './nodes/event-node.js';
export { FunctionEntryNode } from './nodes/function-entry-node.js';
export { FunctionReturnNode } from './nodes/function-return-node.js';
export { RerouteNode } from './nodes/reroute-node.js';
export { TraySpawnChoiceNode } from './nodes/tray-spawn-choice-node.js';
export { ValueNode } from './nodes/value-node.js';
export { GetVariableNode } from './nodes/get-variable-node.js';
export { SetVariableNode } from './nodes/set-variable-node.js';
export { VariableStore } from './variable-store.js';
export { instantiateGraph } from './instantiate-graph.js';
export { foldConstantValueSubtrees } from './constant-fold.js';
export { setGraphErrorReporter, toErrorMessage } from './errors.js';
export const runtimeVersion = '0.0.0';
function buildLocalVariablesByFunction(variables) {
    const byFunction = {};
    for (const variable of variables) {
        if (variable.scope !== 'local' || !variable.functionId)
            continue;
        if (!byFunction[variable.functionId])
            byFunction[variable.functionId] = {};
        byFunction[variable.functionId][variable.id] = variable.defaultValue;
    }
    return byFunction;
}
function buildGraphStoreDefinitions(variables) {
    return variables
        .filter((variable) => variable.scope !== 'local')
        .map((variable) => ({
        id: variable.id,
        defaultValue: variable.defaultValue,
        isConst: variable.isConst === true
    }));
}
export class RuntimeEngine {
    nodes;
    connections;
    graphID;
    graphName;
    subGraphs;
    variableStore;
    constructor() {
        this.nodes = null;
        this.connections = null;
        this.graphID = null;
        this.graphName = null;
        this.subGraphs = {};
        this.variableStore = null;
    }
    async init(graph, nodeData = {}, subGraphs = {}, options = {}) {
        this.subGraphs = subGraphs;
        const variables = options.variables ?? [];
        this.variableStore = new VariableStore(buildGraphStoreDefinitions(variables));
        const enrichedNodeData = {
            ...nodeData,
            variableStore: this.variableStore,
            localVariablesByFunction: buildLocalVariablesByFunction(variables),
            subGraphs: {
                ...(nodeData.subGraphs ?? {}),
                ...subGraphs,
            },
        };
        const instance = instantiateGraph(graph, enrichedNodeData);
        this.nodes = instance.nodes;
        this.graphID = graph.id;
        this.graphName = graph.name;
        this.connections = graph.connections;
        foldConstantValueSubtrees(this.nodes, this.variableStore);
        for (const node of this.nodes) {
            if (node instanceof EventNode) {
                node.run();
            }
        }
    }
    initOutConnections(currentNode, connections) {
        const isNextConnection = (connection) => connection.source.nodeId === currentNode.id;
        const nextConnections = connections.filter(isNextConnection);
        for (const connection of nextConnections) {
            const nextNode = this.getNodeById(connection.target.nodeId);
            if (!nextNode)
                continue;
            const outKey = toCamelCase(connection.source.portKey);
            if (!currentNode.outConnections.has(outKey)) {
                currentNode.outConnections.set(outKey, []);
            }
            currentNode.outConnections.get(outKey).push({
                connectedNode: nextNode,
                inPortKey: toCamelCase(connection.target.portKey),
            });
        }
    }
    initInConnections(currentNode, connections) {
        const isPrevConnection = (connection) => connection.target.nodeId === currentNode.id;
        const previousConnections = connections.filter(isPrevConnection);
        for (const connection of previousConnections) {
            const previousNode = this.getNodeById(connection.source.nodeId);
            if (!previousNode)
                continue;
            const inKey = toCamelCase(connection.target.portKey);
            if (!currentNode.inConnections.has(inKey)) {
                currentNode.inConnections.set(inKey, []);
            }
            currentNode.inConnections.get(inKey).push({
                connectedNode: previousNode,
                outPortKey: toCamelCase(connection.source.portKey),
            });
        }
    }
    getNodeById(id) {
        return this.nodes.find((node) => node.id === id);
    }
    toCamelCase(name) {
        return toCamelCase(name);
    }
}
